import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ShoppingCart, ClipboardList } from "lucide-react";

const STATUSES = ["Placed", "Confirmed", "Preparing", "Ready", "Shipped", "Completed", "Cancelled"];
const PAY_LABEL = { pending_approval: "Pending Approval", partially_paid: "Partially Paid", paid: "Paid", rejected: "Rejected", unpaid: "Unpaid", due: "Due" };
const PAY_CLASS = { paid: "text-emerald-600", pending_approval: "text-amber-600", partially_paid: "text-amber-600", rejected: "text-destructive", unpaid: "text-muted-foreground", due: "text-amber-600" };

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [selected, setSelected] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/store/orders", {
      params: { search: search || undefined, status: status === "all" ? undefined : status },
    });
    setOrders(data);
  }, [search, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSelected = orders.length > 0 && selected.length === orders.length;
  const toggleAll = () => setSelected(allSelected ? [] : orders.map((o) => o.id));

  const plan = () => {
    sessionStorage.setItem("drelik_plan_ids", JSON.stringify(selected));
    navigate("/dashboard/orders/plan");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Orders</h1>
          <p className="text-muted-foreground mt-1">Track and fulfill customer orders.</p>
        </div>
        {selected.length > 0 && (
          <Button className="rounded-lg" onClick={plan} data-testid="plan-selected-btn"><ClipboardList className="h-4 w-4 mr-1.5" /> Plan {selected.length} selected</Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by order # or customer..." className="pl-9 h-11" data-testid="order-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-11 w-full sm:w-52" data-testid="order-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-xl border-border overflow-hidden">
        {orders.length === 0 ? (
          <div className="p-12 text-center">
            <ShoppingCart className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="font-medium">No orders found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-orders" /></TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Payment Status</TableHead>
                <TableHead>Order Status</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.includes(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`select-order-${o.id}`} />
                  </TableCell>
                  <TableCell className="font-medium cursor-pointer" onClick={() => navigate(`/dashboard/orders/${o.id}`)}>{o.order_number}</TableCell>
                  <TableCell className="cursor-pointer" onClick={() => navigate(`/dashboard/orders/${o.id}`)}>{o.customer_name}</TableCell>
                  <TableCell className="capitalize text-sm text-muted-foreground">{o.payment_method}</TableCell>
                  <TableCell><span className={`text-sm font-medium ${PAY_CLASS[o.payment_status] || ""}`}>{PAY_LABEL[o.payment_status] || o.payment_status}</span></TableCell>
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  <TableCell className="text-right font-semibold">{money(o.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
